"use client";

import { useCallback, useMemo, useEffect } from "react";
import {
  InterviewEventType,
  validateInterviewEventPayload,
} from "@/app/api/analytics/schema";
import { usePostHog } from "posthog-js/react";
import { usePathname, useSearchParams } from "next/navigation";
import { URLSearchParams } from "url";

interface UseCustomAnalyticsParams {
  userId: string;
  companyId: string;
  callId?: string;
  contextProperties?: Record<string, unknown>;
}

export function useCustomAnalytics({
  userId,
  companyId,
  callId,
  contextProperties,
}: UseCustomAnalyticsParams) {
  const posthog = usePostHog();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const userContext = useMemo(
    () => ({
      org_userId: userId,
      userId,
      companyId,
      ...contextProperties,
    }),
    [userId, companyId, contextProperties],
  );

  const captureInternalEvent = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      if (!posthog) return;
      posthog.capture(eventName, {
        ...userContext,
        ...(properties ?? {}),
      });
    },
    [posthog, userContext],
  );

  const identifyUser = useCallback(() => {
    if (!posthog || !userId) {
      return;
    }

    posthog.identify(userId, {
      name: `Candidate ${userId}`,
      ...userContext,
    });
  }, [posthog, userId, userContext]);

  const sendEvent = useCallback(
    async (eventType: InterviewEventType, eventCallId?: string) => {
      try {
        const payload = {
          callId: eventCallId ?? callId,
          userId,
          companyId,
          eventType,
          timestamp: Date.now(),
        };

        const validatedPayload = validateInterviewEventPayload(payload);

        captureInternalEvent(eventType, validatedPayload);

        const query = new URLSearchParams();
        const interviewToken = searchParams.get("i");
        if (interviewToken) {
          query.append("interview_token", interviewToken);
        }

        const response = await fetch(`/api/analytics?${query.toString()}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(validatedPayload),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to send interview event");
        }

        return {
          success: true,
          message: data.message,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      }
    },
    [userId, companyId, callId, captureInternalEvent],
  );

  const candidateIntentionGetStarted = useCallback(() => {
    captureInternalEvent("candidate_intention_get_started");
  }, [captureInternalEvent]);

  const setup = useCallback(() => {
    if (!posthog || posthog.config?.token) {
      return;
    }
    console.log("Attempting 🦔");
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY as string;
    const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
    if (!posthogKey || !posthogHost) {
      console.warn("🦔 PostHog key or host not found");
      return;
    }
    posthog.init(posthogKey, {
      api_host: posthogHost, //"/ingest",

      //ui_host: "https://us.posthog.com",
      loaded: (posthog) => {
        // console.log("🦔 loaded callback triggered");
        if (process.env.NODE_ENV === "development") {
          console.log(
            "Development environment detected, disabling 🦔 capturing",
          );
          posthog.opt_out_capturing();
          posthog.set_config({ disable_session_recording: true });
        }
      },
      person_profiles: "always",
      persistence: "cookie",
      //xhr_headers: { "Should-Proxy": "true" },
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
    });
    console.log("🦔 init");
  }, [posthog]);

  const trackPageView = useCallback(
    (pageName: string, properties?: Record<string, unknown>) => {
      let url = window.origin + pathname;
      if (searchParams.toString()) {
        url = url + `?${searchParams.toString()}`;
      }

      captureInternalEvent("$pageview", {
        page: pageName,
        $current_url: url,
        ...(properties ?? {}),
      });
    },
    [captureInternalEvent, pathname, searchParams],
  );

  const trackMicrophonePermissionIntent = useCallback(() => {
    captureInternalEvent("candidate_intention_microphone_permission");
  }, [captureInternalEvent]);

  const trackMicrophonePermissionSuccess = useCallback(() => {
    captureInternalEvent("microphone_permission_success");
  }, [captureInternalEvent]);

  const trackCameraPermissionIntent = useCallback(() => {
    captureInternalEvent("candidate_intention_camera_permission");
  }, [captureInternalEvent]);

  const trackCameraPermissionSuccess = useCallback(() => {
    captureInternalEvent("camera_permission_success");
  }, [captureInternalEvent]);

  useEffect(() => {
    setup();
  }, [setup]);

  return {
    identifyUser,
    trackPageView,
    trackMicrophonePermissionIntent,
    trackMicrophonePermissionSuccess,
    trackCameraPermissionIntent,
    trackCameraPermissionSuccess,
    interviewStarted: (callId: string) =>
      sendEvent("interview_started", callId),
    interviewEnded: (callId: string) => sendEvent("interview_ended", callId),
    candidateIntentionGetStarted,
    candidateIntentionStartInterview: () =>
      sendEvent("candidate_intention_start_interview"),
    candidateIntentionEndInterview: () =>
      sendEvent("candidate_intention_end_interview"),
    candidateIntentionSkipIntroVideo: () =>
      sendEvent("candidate_intention_skip_intro_video"),
  };
}
